import React, { useMemo } from 'react';
import styled from 'styled-components';
import { useRecoilState, useRecoilValue } from 'recoil';
import { SelectedGroupsAtomState, ListAtomState } from '../../../recoil/ModalAtom';
import { formatManWon } from '../../../utils/formatNumber';
import {
  getSectionKey,
  SECTION_META,
  SECTION_CUSTOM,
  SECTION_TEMPLATE,
} from '../../utils/groupSection';

const REQUIRED_GROUP_KEY = 'infraSetting';

const SummaryWrap = styled.aside`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 88px;
  align-self: flex-start;
  max-height: calc(100vh - 100px);
  overflow-y: auto;

  @media screen and (max-width: 1024px) {
    width: 100%;
    position: static;
    max-height: none;
    align-self: stretch;
  }
`;

const Card = styled.div`
  background: ${p => p.theme.bgCard};
  border: 1px solid ${p => p.theme.border};
  border-radius: ${p => p.theme.radius};
  padding: 16px;
`;

const SectionSubTitle = styled.h4`
  margin: 14px 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: ${p => p.theme.textSecondary};
`;

const CardTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.text};
`;

const CountBadge = styled.span`
  font-size: 12px;
  color: ${p => p.theme.accent};
  font-weight: 600;
`;

const GroupRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background: ${p => p.theme.bgInput};
  border-radius: ${p => p.theme.radiusSm};
  margin-bottom: 8px;
  font-size: 13px;
  color: ${p => p.theme.text};

  &:last-child {
    margin-bottom: 0;
  }
`;

const GroupLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
`;

const RequiredTag = styled.span`
  font-size: 10px;
  background: ${p => p.theme.bgRequired};
  color: ${p => p.theme.textRequired};
  padding: 2px 6px;
  border-radius: 4px;
  font-weight: 600;
`;

const PriceText = styled.span`
  font-weight: 700;
  color: ${p => p.$zero ? p.theme.textMuted : p.theme.text};
  font-size: 13px;
  white-space: nowrap;
`;

const ResetAllButton = styled.button`
  width: 100%;
  padding: 8px;
  border: 1px solid ${p => p.theme.border};
  background: ${p => p.theme.bgCard};
  color: ${p => p.theme.textSecondary};
  border-radius: ${p => p.theme.radiusSm};
  cursor: pointer;
  font-size: 12px;
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 13px;
  color: ${p => p.theme.textSecondary};
  border-bottom: 1px dashed ${p => p.theme.border};

  &:last-child {
    border-bottom: none;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-top: 12px;
  margin-top: 8px;
  border-top: 1px solid ${p => p.theme.border};
`;

const TotalLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.text};
`;

const TotalAmount = styled.span`
  font-size: 24px;
  font-weight: 800;
  color: ${p => p.theme.accent};
`;

const VatNote = styled.div`
  text-align: right;
  font-size: 11px;
  color: ${p => p.theme.textMuted};
  margin-top: 2px;
`;

const InfoBox = styled.div`
  background: ${p => p.theme.accentLight};
  color: ${p => p.theme.accent};
  font-size: 12px;
  padding: 12px;
  border-radius: ${p => p.theme.radiusSm};
  line-height: 1.6;
  display: flex;
  gap: 8px;
`;

export default function RightSummary({ groupList, onResetAll }) {
  const [selectedGroups, setSelectedGroups] = useRecoilState(SelectedGroupsAtomState);
  const itemStack = useRecoilValue(ListAtomState);

  const groupTotals = useMemo(() => {
    const totals = {};
    selectedGroups.forEach(g => { totals[g] = 0; });
    itemStack.forEach(item => {
      if (totals[item.GROUP] !== undefined) {
        totals[item.GROUP] += Number(item.PRICE) || 0;
      }
    });
    return totals;
  }, [selectedGroups, itemStack]);

  const total = useMemo(() => {
    return Object.values(groupTotals).reduce((sum, v) => sum + v, 0);
  }, [groupTotals]);

  const groupNameMap = useMemo(() => {
    const map = {};
    groupList.forEach(g => { map[g.std_key] = g.std_value; });
    return map;
  }, [groupList]);

  const handleResetAll = () => {
    setSelectedGroups([REQUIRED_GROUP_KEY]);
    onResetAll?.();
  };

  const renderGroupRow = (key) => {
    const isRequired = key === REQUIRED_GROUP_KEY;
    const value = groupTotals[key] || 0;
    return (
      <GroupRow key={key}>
        <GroupLabel>
          {groupNameMap[key] || key}
          {isRequired && <RequiredTag>기본 제공</RequiredTag>}
        </GroupLabel>
        <PriceText $zero={value === 0}>
          {value === 0 ? '0원' : `+ ${formatManWon(value)}만원`}
        </PriceText>
      </GroupRow>
    );
  };

  const customKeys = selectedGroups.filter(k => getSectionKey(k) === SECTION_CUSTOM);
  const templateKeys = selectedGroups.filter(k => getSectionKey(k) === SECTION_TEMPLATE);

  return (
    <SummaryWrap>
      <Card>
        <CardTitleRow>
          <CardTitle>선택된 그룹</CardTitle>
          <CountBadge>{selectedGroups.length}개 선택됨</CountBadge>
        </CardTitleRow>

        {customKeys.length > 0 && (
          <>
            <SectionSubTitle>{SECTION_META[SECTION_CUSTOM].title}</SectionSubTitle>
            {customKeys.map(renderGroupRow)}
          </>
        )}

        {templateKeys.length > 0 && (
          <>
            <SectionSubTitle>{SECTION_META[SECTION_TEMPLATE].title}</SectionSubTitle>
            {templateKeys.map(renderGroupRow)}
          </>
        )}

        <ResetAllButton onClick={handleResetAll}>전체 초기화</ResetAllButton>
      </Card>

      <Card>
        <CardTitle>견적 요약</CardTitle>
        <div style={{ marginTop: 12 }}>
          <SummaryRow>
            <span>기본 금액</span>
            <span>0원</span>
          </SummaryRow>
          <SummaryRow>
            <span>선택 그룹</span>
            <span>+ {formatManWon(total)}만원</span>
          </SummaryRow>
          <TotalRow>
            <TotalLabel>예상 견적 합계</TotalLabel>
            <TotalAmount>{formatManWon(total)}만원</TotalAmount>
          </TotalRow>
          <VatNote>(VAT 별도)</VatNote>
        </div>
      </Card>

      <InfoBox>
        선택한 그룹에 따라 견적이 변경될 수 있습니다. 다음 단계에서 상세 항목을 선택해주세요.
      </InfoBox>
    </SummaryWrap>
  );
}
